import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { HOTEL_SEARCH_TYPES } from 'constants/offer'
import { queryKeyTypeaheadId } from 'constants/url'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'
import { encodeSearchParams } from 'lib/search/searchUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import { buildUrl } from 'lib/url/searchUrlUtils'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import BodyText from 'LuxKit/Typography/BodyText'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import { useSearchPlace } from 'tripPlanner/hooks/api/place'
import BrowseAllResultsFooter from '../BrowseAllResultsFooter'
import HotelListingDrawerHeader from './HotelListingDrawerHeader'
import DrawerHotelTile from './DrawerHotelTile'
import DrawerHotelTileSkeleton from './DrawerHotelTileSkeleton'
import HotelListingEmptyState from './HotelListingEmptyState'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import HotelDetailView from './HotelDetailView'
import useHotelSearch from './useHotelSearch'
import { applyOfferSummaryMemberPriceFallback, applySavedHotelPriceFallback, mapOfferDetailToDrawerHotel, mapOfferToDrawerHotelTileFeatures, mergeDrawerHotelWithOfferDetail } from './mapOfferDetailToDrawerHotel'
import type { HotelListingDrawerProps, DrawerHotel } from './types'
import { formatDrawerHotelInfo } from './formatDrawerHotelInfo'
import DrawerAskAIFooter from 'tripPlanner/components/DrawerAskAI/DrawerAskAIFooter'
import useOfferDetails from '../../hooks/useOfferDetails'
import useOfferReviews from '../../hooks/useOfferReviews'
import { OFFER_DETAIL_STALE_TIME } from 'tripPlanner/hooks/api/queryConstants'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import { isOfferBookedInItinerary, getOfferItineraryDayRange, getOfferSavedBooking } from 'tripPlanner/utils/chatItinerary'
import { useOfferBookingState } from 'tripPlanner/hooks/useOfferBookingState'
import { getOfferDetail } from 'tripPlanner/api'
import { getOffersById } from 'api/offer'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import * as OfferDetailKeys from 'tripPlanner/hooks/reactQueryKeys/offerDetail'
import * as OfferKeys from 'tripPlanner/hooks/reactQueryKeys/offers'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useTripPlannerOfferListTracking from '../../hooks/useTripPlannerOfferListTracking'

const NonInteractiveChipWrapper = styled.div`
  pointer-events: none;
`

const SKELETON_COUNT = 3
const DEFAULT_OCCUPANCIES: Array<App.Occupants> = [{ adults: 2 }]

function isHotelSearchType(offerType?: string): offerType is App.OfferType {
  return offerType !== undefined && HOTEL_SEARCH_TYPES.some((hotelSearchType) => hotelSearchType === offerType)
}

function isTileSummaryOffer(
  offer: Awaited<ReturnType<typeof getOffersById>>[number],
): offer is App.HotelOffer | App.BedbankOffer {
  return isLEHotel(offer) || isBedbankOffer(offer)
}

function normalizePlaceName(value?: string) {
  return value?.trim().toLowerCase()
}

const FullWidthLoadingBox = styled(LoadingBox)`
  width: 100%;
`

export const ContentWrapper = styled.div`
  margin: 0 auto;
  padding: ${rem(24)};
  width: ${rem(800)};
  max-width: 100%;
  box-sizing: border-box;
`

function HotelListingDrawer({
  isOpen,
  onClose,
  placeName,
  placeCountry,
  stopStartDate,
  stopEndDate,
  stopCoordinates,
  contextStop,
  onShowMap,
  onExploreMap,
  itineraryData,
  onSendMessage,
}: HotelListingDrawerProps) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(undefined)
  const [selectedOfferType, setSelectedOfferType] = useState<string | undefined>(undefined)
  const [typeaheadId] = useState(() => uuidV4())
  const [drawerSessionKey, setDrawerSessionKey] = useState(() => uuidV4())
  const wasOpenRef = useRef(false)
  const searchQuery = `${placeName}, ${placeCountry}`

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setDrawerSessionKey(uuidV4())
    }

    wasOpenRef.current = isOpen
  }, [isOpen])

  // Stop dates are the default checkIn/checkOut for search + drawer.
  // Fall back to deriving from the stop's days when the top-level startDate/endDate
  // aren't set (e.g. the AI hasn't assigned calendar dates at the stop level yet).
  const checkIn = useMemo(() => {
    if (stopStartDate) return stopStartDate
    return contextStop?.days.find(d => d.date)?.date
  }, [stopStartDate, contextStop])

  const checkOut = useMemo(() => {
    if (stopEndDate) return stopEndDate
    if (!contextStop?.days.length) return undefined
    const datedDays = contextStop.days.filter(d => !!d.date)
    return datedDays.length ? datedDays[datedDays.length - 1].date : undefined
  }, [stopEndDate, contextStop])

  // Stop duration pre-selects the nights slider in the "Add to trip" modal
  // when opened from inside this drawer (tile or detail view).
  const filterNights = useMemo(() => {
    if (!checkIn || !checkOut) return undefined
    const nights = moment(checkOut).diff(moment(checkIn), 'days')
    return nights > 0 ? nights : undefined
  }, [checkIn, checkOut])

  const { data: destinationPlace, isFetched: isDestinationPlaceFetched } = useSearchPlace(
    {
      lat: stopCoordinates?.lat,
      lng: stopCoordinates?.lng,
      type: 'city',
    },
    {
      enabled: isOpen && !!stopCoordinates,
      staleTime: Infinity,
    },
  )

  const shouldUseResolvedDestination = useMemo(() => {
    const normalizedStopPlaceName = normalizePlaceName(placeName)
    const normalizedResolvedPlaceName = normalizePlaceName(destinationPlace?.name)

    if (!destinationPlace?.id || !normalizedStopPlaceName || !normalizedResolvedPlaceName) {
      return false
    }

    return normalizedStopPlaceName === normalizedResolvedPlaceName ||
      normalizedStopPlaceName.includes(normalizedResolvedPlaceName) ||
      normalizedResolvedPlaceName.includes(normalizedStopPlaceName)
  }, [destinationPlace?.id, destinationPlace?.name, placeName])

  const resolvedDestinationPlaceId = shouldUseResolvedDestination ? destinationPlace?.id : undefined
  const isHotelSearchReady = !stopCoordinates || isDestinationPlaceFetched

  const { hotels, isLoading, hasMore, isLoadingMore, loadMore } = useHotelSearch({
    searchQuery,
    placeId: resolvedDestinationPlaceId,
    region: geo.currentRegionCode,
    checkIn,
    checkOut,
    enabled: isOpen && isHotelSearchReady,
  })
  const hotelOfferIds = useMemo(() => hotels.map((hotel) => hotel.offerId), [hotels])
  const { data: tileOffers = [] } = useQuery({
    queryKey: OfferKeys.collection(geo.currentRegionCode, [
      checkIn ?? '',
      checkOut ?? '',
      ...hotelOfferIds,
    ]),
    queryFn: () => getOffersById(
      hotelOfferIds,
      geo.currentRegionCode,
      {
        checkIn,
        checkOut,
        occupancies: DEFAULT_OCCUPANCIES,
      },
    ),
    enabled: isOpen && hotelOfferIds.length > 0,
    staleTime: OFFER_DETAIL_STALE_TIME,
    keepPreviousData: true,
  })
  const tileSummaryOffers = useMemo<Array<App.HotelOffer | App.BedbankOffer>>(
    () => tileOffers.filter(isTileSummaryOffer),
    [tileOffers],
  )
  const trackingOfferById = useMemo(() => {
    return new Map(tileSummaryOffers.map((offer) => [offer.id, offer]))
  }, [tileSummaryOffers])
  const tileDetailHotels = useMemo(
    () => hotels.filter((hotel): hotel is DrawerHotel & { offerType: App.OfferType } => isHotelSearchType(hotel.offerType)),
    [hotels],
  )
  const tileDetailQueries = useQueries({
    queries: tileDetailHotels.map((hotel) => ({
      queryKey: OfferDetailKeys.detail(
        hotel.offerId,
        geo.currentRegionCode,
        undefined,
        hotel.offerType,
        checkIn,
        checkOut,
      ),
      queryFn: () => getOfferDetail({
        offerId: hotel.offerId,
        region: geo.currentRegionCode,
        offerType: hotel.offerType,
        checkIn,
        checkOut,
      }),
      enabled: isOpen,
      staleTime: OFFER_DETAIL_STALE_TIME,
    })),
  })
  const hotelsWithTileDetails = useMemo(() => {
    const summaryTileFeaturesByOfferId = new Map<string, Pick<DrawerHotel, 'inclusions' | 'tileAmenities'>>(
      tileSummaryOffers.map((offer) => [
        offer.id,
        mapOfferToDrawerHotelTileFeatures(offer),
      ]),
    )
    const detailsByOfferId = new Map<string, Pick<DrawerHotel, 'rating' | 'reviewsTotal' | 'inclusions' | 'amenities'>>()

    tileDetailQueries.forEach((query, index) => {
      const hotel = tileDetailHotels[index]
      if (!hotel || !query.data) {
        return
      }

      const detailHotel = mergeDrawerHotelWithOfferDetail(hotel, query.data)

      detailsByOfferId.set(hotel.offerId, {
        rating: detailHotel.rating,
        reviewsTotal: detailHotel.reviewsTotal,
        inclusions: detailHotel.inclusions,
        amenities: detailHotel.amenities,
      })
    })

    return hotels.map((hotel) => {
      const summaryTileFeatures = summaryTileFeaturesByOfferId.get(hotel.offerId)
      const details = detailsByOfferId.get(hotel.offerId)

      if (!details && !summaryTileFeatures) {
        return hotel
      }

      return {
        ...hotel,
        rating: details?.rating ?? hotel.rating,
        reviewsTotal: details?.reviewsTotal ?? hotel.reviewsTotal,
        inclusions: details?.inclusions?.length ? details.inclusions : summaryTileFeatures?.inclusions ?? hotel.inclusions,
        tileAmenities: summaryTileFeatures?.tileAmenities ?? hotel.tileAmenities,
        amenities: details?.amenities ?? hotel.amenities,
      }
    })
  }, [hotels, tileDetailHotels, tileDetailQueries, tileSummaryOffers])

  const savedBooking = useMemo(
    () => getOfferSavedBooking(selectedOfferId, itineraryData),
    [selectedOfferId, itineraryData],
  )

  const { savedBookedRooms, detailOrderId, orderIdForLink } = useOfferBookingState(
    selectedOfferId ?? undefined,
    itineraryData,
  )

  const { offerDetail, isLoading: isDetailLoading, isError: isDetailError, refetch: refetchDetail } = useOfferDetails({
    offerId: selectedOfferId,
    region: geo.currentRegionCode,
    offerType: selectedOfferType,
    checkIn,
    checkOut,
    orderId: detailOrderId,
    enabled: !!selectedOfferId,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: selectedOfferId,
    region: geo.currentRegionCode,
    enabled: !!selectedOfferId && !!offerDetail,
  })

  const detailSummaryOffer = useMemo(
    () => tileSummaryOffers.find(offer => offer.id === selectedOfferId),
    [tileSummaryOffers, selectedOfferId],
  )

  const detailHotel = useMemo(() => {
    if (!offerDetail) return undefined
    let hotel = mapOfferDetailToDrawerHotel(offerDetail)
    hotel = applyOfferSummaryMemberPriceFallback(hotel, detailSummaryOffer)
    hotel = applySavedHotelPriceFallback(hotel, itineraryData)
    return hotel
  }, [offerDetail, itineraryData, detailSummaryOffer])

  const handleSelectHotel = useCallback((hotel: DrawerHotel) => {
    setSelectedOfferId(hotel.offerId)
    setSelectedOfferType(hotel.offerType)
  }, [])

  const handleBack = useCallback(() => {
    setSelectedOfferId(undefined)
  }, [])

  const handleClose = useCallback(() => {
    setSelectedOfferId(undefined)
    onClose()
  }, [onClose])

  const handleAfterClose = useCallback(() => {
    setSelectedOfferId(undefined)
    setHasLoadMoreTriggerLeftView(false)
  }, [])

  const tracking = useTripPlannerOfferListTracking(
    'hotelSearchDrawer',
    `${drawerSessionKey}:${searchQuery}:${checkIn ?? ''}:${checkOut ?? ''}`,
  )

  const browseAllResultsUrl = useMemo(() => {
    const searchParams = encodeSearchParams({
      urlSearch: '',
      dates: {
        checkIn,
        checkOut,
      },
      rooms: DEFAULT_OCCUPANCIES,
      destinationId: shouldUseResolvedDestination ? destinationPlace?.id : undefined,
      destinationName: shouldUseResolvedDestination ? destinationPlace?.name : undefined,
    })

    if (!shouldUseResolvedDestination) {
      searchParams.set('query', searchQuery)
      searchParams.set(queryKeyTypeaheadId, typeaheadId)
    }

    return buildUrl('/search', geo.currentRegionCode, searchParams.toString())
  }, [checkIn, checkOut, destinationPlace?.id, destinationPlace?.name, geo.currentRegionCode, searchQuery, shouldUseResolvedDestination, typeaheadId])
  const hasHotels = hotelsWithTileDetails.length > 0
  const isDetailView = !!selectedOfferId
  const [loadMoreTriggerRef, isLoadMoreTriggerInView] = useInView({
    threshold: 0,
    rootMargin: '0px 0px 160px 0px',
  })
  const [hasLoadMoreTriggerLeftView, setHasLoadMoreTriggerLeftView] = useState(false)

  useEffect(() => {
    if (isDetailView || !hasMore) {
      setHasLoadMoreTriggerLeftView(false)
      return
    }

    if (!isLoadMoreTriggerInView && !isLoadingMore) {
      setHasLoadMoreTriggerLeftView(true)
    }
  }, [hasMore, isDetailView, isLoadMoreTriggerInView, isLoadingMore])

  useEffect(() => {
    if (!isDetailView && hasMore && !isLoadingMore && isLoadMoreTriggerInView && hasLoadMoreTriggerLeftView) {
      setHasLoadMoreTriggerLeftView(false)
      loadMore()
    }
  }, [hasLoadMoreTriggerLeftView, hasMore, isDetailView, isLoadMoreTriggerInView, isLoadingMore, loadMore])

  const handleShowMapFromHeader = useCallback(() => {
    if (detailHotel) {
      onShowMap(detailHotel)
    }
  }, [onShowMap, detailHotel])

  const dayRangeLabel = useMemo(() => {
    if (!selectedOfferId) return undefined
    const range = getOfferItineraryDayRange(selectedOfferId, itineraryData)
    return range?.label
  }, [selectedOfferId, itineraryData])

  const isBooked = isOfferBookedInItinerary(selectedOfferId ?? undefined, itineraryData)

  const detailHeaderActions = useMemo(() => {
    if (!isDetailView || !detailHotel) return undefined
    const hasGeo = !!detailHotel.geoLocation
    const showMapLabel = intl.formatMessage({
      id: 'uyDWABuVrlw97fTIbbjL',
      defaultMessage: 'Show map',
      description: 'Button label to show hotel location on map in hotel detail view',
    })
    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasGeo && (
          <FilterChip size="small" startIcon={<LineMapIcon />} onClick={handleShowMapFromHeader}>
            {showMapLabel}
          </FilterChip>
        )}
        {isBooked && dayRangeLabel && (
          <NonInteractiveChipWrapper>
            <FilterChip size="small">
              {dayRangeLabel}
            </FilterChip>
          </NonInteractiveChipWrapper>
        )}
        {onSendMessage && !isBooked && (
          <AddHotelToTripButton
            itineraryData={itineraryData}
            hotelName={detailHotel.name}
            offerId={detailHotel.offerId}
            offerType={detailHotel.offerType}
            hotelCity={detailHotel.location}
            hotelCoordinates={detailHotel.geoLocation}
            contextStop={contextStop}
            onSendMessage={onSendMessage}
            onAfterAction={handleClose}
            variant="outline"
            size="small"
          />
        )}
      </Group>
    )
  }, [isDetailView, detailHotel, handleShowMapFromHeader, dayRangeLabel, intl, onSendMessage, isBooked, itineraryData, contextStop, handleClose])

  const askAIFooter = useMemo(() => {
    if (!isDetailView || !detailHotel) return undefined
    return (
      <DrawerAskAIFooter
        contextLabel={detailHotel.name}
        isOpen={isOpen}
        hotelDetails={formatDrawerHotelInfo(detailHotel)}
      />
    )
  }, [isDetailView, detailHotel, isOpen])

  const browseAllResultsFooter = useMemo(() => {
    if (isDetailView || !hasHotels) return undefined

    return (
      <BrowseAllResultsFooter
        heading={intl.formatMessage({
          id: 'QFGNPlPZs7ycwnBOsTQA',
          defaultMessage: 'There’s plenty more to explore',
          description: 'Prompt heading inviting users to browse all hotel search results',
        })}
        body={intl.formatMessage({
          id: 'YC1EB2OcYdrQD1ci1sXS',
          defaultMessage: 'Browse all available hotels for this destination.',
          description: 'Supporting text encouraging users to view more hotel search results',
        })}
        browseAllResultsUrl={browseAllResultsUrl}
        buttonLabel={intl.formatMessage({
          id: 'itl8WEctxs1Z0nf23fD3',
          defaultMessage: 'Browse all results',
          description: 'Button label linking to the full hotel search results page',
        })}
      />
    )
  }, [browseAllResultsUrl, hasHotels, intl, isDetailView])

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={handleClose}
      onAfterClose={handleAfterClose}
      onBack={isDetailView ? handleBack : undefined}
      direction="right-to-left"
      footer={askAIFooter ?? browseAllResultsFooter}
      headerActions={detailHeaderActions}
    >
      {isDetailView && (
        <>
          {isDetailLoading && !detailHotel && (
            <ContentWrapper>
              <Group direction="vertical" gap={16}>
                <FullWidthLoadingBox height={200} borderRadius="M" />
                <LoadingBox height={28} width={240} borderRadius="S" />
                <LoadingBox height={20} width={160} borderRadius="S" />
              </Group>
            </ContentWrapper>
          )}
          {isDetailError && !isDetailLoading && !detailHotel && (
            <ContentWrapper>
              <Group direction="vertical" gap={8} horizontalAlign="center" verticalAlign="center">
                <BodyText variant="medium" colour="critical">
                  {intl.formatMessage({
                    id: 'AyMdUB-fKPPyurSXTes2',
                    defaultMessage: 'Failed to load hotel details.',
                    description: 'Error message shown when hotel offer details fail to load in the drawer',
                  })}
                </BodyText>
                <TextButton kind="secondary" size="small" onClick={() => refetchDetail()}>
                  {intl.formatMessage({
                    id: 'q2nIV5-Sco41UZtxNQNg',
                    defaultMessage: 'Try again',
                    description: 'Button label to retry loading hotel offer details after an error',
                  })}
                </TextButton>
              </Group>
            </ContentWrapper>
          )}
          {detailHotel && (
            <HotelDetailView
              hotel={detailHotel}
              reviews={reviews}
              reviewsLoading={reviewsLoading}
              isBooked={isBooked}
              orderId={orderIdForLink}
              savedBookedRooms={savedBookedRooms}
              itineraryData={itineraryData}
              contextStop={contextStop}
              onSendMessage={onSendMessage}
              onAfterAction={handleClose}
              checkIn={savedBooking?.checkIn ?? checkIn}
              checkOut={savedBooking?.checkOut ?? checkOut}
              initialOccupancies={savedBooking?.occupancies}
              selectedPackageId={savedBooking?.packageId}
              selectedRoomTypeId={savedBooking?.roomTypeId}
              selectedRateId={savedBooking?.rateId}
              selectedCheckIn={savedBooking?.checkIn}
              selectedCheckOut={savedBooking?.checkOut}
            />
          )}
        </>
      )}

      {!isDetailView && (
        <ContentWrapper>
          <Group direction="vertical" gap={24}>
            <HotelListingDrawerHeader
              placeName={placeName}
              onExploreMap={onExploreMap}
            />

            {isLoading && (
              <Group direction="vertical" gap={16}>
                {Array.from({ length: SKELETON_COUNT }, (_, index) => (
                  <DrawerHotelTileSkeleton key={index} />
                ))}
              </Group>
            )}

            {!isLoading && !hasHotels && (
              <HotelListingEmptyState placeName={placeName} searchUrl={browseAllResultsUrl} />
            )}

            {!isLoading && hasHotels && (
              <>
                <OfferListEventsProvider tracking={tracking}>
                  <Group direction="vertical" gap={16}>
                    {hotelsWithTileDetails.map((hotel, index) => (
                      <DrawerHotelTile
                        key={hotel.id}
                        hotel={hotel}
                        onSelectHotel={handleSelectHotel}
                        position={index}
                        trackingOffer={trackingOfferById.get(hotel.offerId)}
                        itineraryData={itineraryData}
                        contextStop={contextStop}
                        onSendMessage={onSendMessage}
                        onAfterAction={handleClose}
                        onShowOnMap={onShowMap}
                        filterNights={filterNights}
                      />
                    ))}
                  </Group>
                </OfferListEventsProvider>

                {isLoadingMore && (
                  <Group direction="vertical" gap={16}>
                    {Array.from({ length: 2 }, (_, index) => (
                      <DrawerHotelTileSkeleton key={`loading-more-${index}`} />
                    ))}
                  </Group>
                )}

                {hasMore && !isLoadingMore && (
                  <Group ref={loadMoreTriggerRef} direction="horizontal" horizontalAlign="center">
                    <TextButton
                      kind="secondary"
                      size="medium"
                      onClick={loadMore}
                    >
                      {intl.formatMessage({
                        id: 'pXLqUwEQiAzarpNypTPc',
                        defaultMessage: 'Show more hotels',
                        description: 'Button label to load more hotel results in the listing drawer',
                      })}
                    </TextButton>
                  </Group>
                )}
              </>
            )}
          </Group>
        </ContentWrapper>
      )}
    </TripDrawer>
  )
}

export default HotelListingDrawer
